/**
 * Preferências do usuário (GET/PUT /preferences — objeto livre, máximo 64 KB).
 *
 * O que vem da API é objeto livre: nada aqui confia nele. Tudo passa por `mergePrefs`, que só aceita
 * chaves conhecidas e descarta o resto — preferência estragada não pode derrubar a tela.
 */
import { DEFAULT_COLUMNS, M } from './metrics';
import { SCREENS, defaultView, type ScreenKey } from './screens';
import type { Currency } from './format';

/** Blocos da Geral, na ordem padrão. `note` é travado: é ele que explica a coorte. */
export const OVERVIEW_BLOCKS = ['kpis', 'chart', 'funnel', 'rates', 'hourly', 'byProject', 'note'] as const;
export type OverviewBlock = (typeof OVERVIEW_BLOCKS)[number];

export const BLOCK_META: Record<OverviewBlock, { l: string; d: string; locked?: boolean }> = {
    kpis: { l: 'Indicadores', d: 'Os números de topo do período.' },
    chart: { l: 'Gráfico principal', d: 'A série do período, no modelo que você escolher.' },
    funnel: { l: 'Funil de entrega', d: 'Do envio ao clique, etapa por etapa.' },
    rates: { l: 'Taxas, dia a dia', d: 'Entrega, abertura e clique, cada uma na sua escala.' },
    hourly: { l: 'As 24 horas', d: 'Somadas no período, pela hora do evento.' },
    byProject: { l: 'Por projeto', d: 'Envios e taxas por projeto — receita não existe aqui.' },
    note: { l: 'Nota da tela', d: 'Explica por que o enviado da Geral é maior que o de Disparos.', locked: true },
};

/**
 * Modelos do gráfico principal da Geral.
 *
 * As três taxas não entram aqui: num eixo só, entrega cola no teto e clique vira uma linha reta no
 * chão. Elas têm bloco próprio (`rates`), com uma escala para cada uma.
 */
export const CHART_MODELS = {
    dual: ['Receita × envios', 'dois eixos: a linha da receita sobre a dos envios'],
    cum: ['Acumulado', 'quanto o período já rendeu, dia após dia'],
    gap: ['Enviado × entregue', 'a área entre as duas curvas é o que não chegou'],
    rpm: ['Receita por mil enviados', 'receita líquida ÷ enviados × 1.000, por dia'],
    engage: ['Aberturas e cliques', 'os dois volumes únicos, dia a dia'],
    split: ['Composição da receita', 'as quatro partes da bruta empilhadas, dia a dia'],
    band: ['Faixa normal', 'a métrica contra o mín–máx dos 7 dias antes; o ponto marca o dia fora'],
    pair: ['Métrica × métrica', 'duas métricas à sua escolha, cada uma com o seu eixo'],
} as const;
export type ChartModel = keyof typeof CHART_MODELS;

/* ----------------------------------------------------------------- telas de lista: blocos */

/**
 * Os blocos de uma tela de lista, na ordem em que são desenhados. A lista é travada: uma tela de
 * lista sem a lista não é nada. Os outros o usuário liga e desliga, como na Geral.
 *
 * Os números destes blocos são a SOMA DAS LINHAS que a lista está mostrando — não os totais do
 * período, que são da Geral e maiores (ela conta envio que não é de disparo).
 */
export const SCREEN_BLOCKS = ['kpis', 'chart', 'funnel', 'list'] as const;
export type ScreenBlock = (typeof SCREEN_BLOCKS)[number];

export const SCREEN_BLOCK_META: Record<ScreenBlock, { l: string; d: string; locked?: boolean }> = {
    kpis: { l: 'Indicadores', d: 'Os números somados das linhas desta lista.' },
    chart: { l: 'Gráfico', d: 'As linhas desenhadas — com seletor de modelo.' },
    funnel: { l: 'Funil de entrega', d: 'Do envio ao clique, somando a lista inteira.' },
    list: { l: 'A lista', d: 'A tabela ou os cards, com busca e colunas.', locked: true },
};

/** Fora da lista, o que vem ligado de saída: o funil é grande demais para abrir sozinho. */
const DEFAULT_HIDDEN_BLOCKS: ScreenBlock[] = ['funnel'];

/** Seis indicadores: dois de dinheiro, dois de volume e duas taxas. */
export const DEFAULT_SCREEN_KPIS = ['net', 'ecpm', 'sent', 'delivered', 'openRate', 'clickRate'];

/* ----------------------------------------------------------------- listas: tabela ou cards */

/**
 * Como a lista desenha cada linha. São os MESMOS dados nos dois: o card não tem número que a
 * tabela não tenha, nem o contrário — muda a forma, não o conteúdo.
 */
export type Display = 'table' | 'cards';
export const DISPLAYS: { k: Display; l: string }[] = [{ k: 'table', l: 'Tabela' }, { k: 'cards', l: 'Cards' }];

/**
 * Os blocos do card, na ordem em que são desenhados — a ordem é fixa; o que o usuário escolhe é
 * quais entram. `ident` é travado: um card sem o nome da linha não diz de quem é.
 */
export const CARD_BLOCKS = ['ident', 'metrics', 'funnel', 'rates', 'extra'] as const;
export type CardBlock = (typeof CARD_BLOCKS)[number];

export const CARD_BLOCK_META: Record<CardBlock, { l: string; d: string; locked?: boolean }> = {
    ident: { l: 'Identificação', d: 'O nome da linha, a situação e as etiquetas de UTM.', locked: true },
    metrics: { l: 'Métricas', d: 'Os números escolhidos. Com “Separar por origem”, uma faixa por bloco.' },
    funnel: { l: 'Funil de entrega', d: 'Enviado, entregue, aberto e clicado, em barras proporcionais.' },
    rates: { l: 'Chips de taxa', d: 'Entrega, abertura e clique, cada uma na sua cor.' },
    extra: { l: 'Bloco da tela', d: 'O que só esta tela tem — os leads do funil, as peças do disparo.' },
};

export type CardSize = 'sm' | 'md' | 'lg';
/** `min` é a largura mínima do card na grade; o resto é o `auto-fill` que decide. */
export const CARD_SIZES: { k: CardSize; l: string; min: number }[] = [
    { k: 'sm', l: 'Compacto', min: 250 },
    { k: 'md', l: 'Médio', min: 330 },
    { k: 'lg', l: 'Largo', min: 440 },
];

/** Quatro números: um de receita, um de volume e duas taxas. Mais que isso o card vira tabela. */
export const DEFAULT_CARD_METRICS = ['net', 'sent', 'openRate', 'clickRate'];

export type KpiStyle = 'cards' | 'grid' | 'list' | 'hero';
export const KPI_STYLES: { k: KpiStyle; l: string }[] = [
    { k: 'cards', l: 'Cards' }, { k: 'grid', l: 'Grade compacta' }, { k: 'list', l: 'Lista' }, { k: 'hero', l: 'Destaque' },
];

/** Indicadores que a Geral oferece: métricas dos totais do período. */
export const KPI_CHOICES = [
    'net', 'rev', 'revshare', 'spread', 'taxRev', 'ecpm', 'gamImp',
    'sent', 'delivered', 'uniqueOpens', 'uniqueClicks', 'bounces', 'unsubscribes',
    'deliveryRate', 'openRate', 'clickRate', 'clickToOpenRate', 'effectiveOpenRate', 'bounceRate',
    'complaintRate', 'unsubscribeRate',
].filter((k) => M[k]);

export const DEFAULT_KPIS = ['net', 'rev', 'sent', 'delivered', 'uniqueOpens', 'uniqueClicks', 'deliveryRate', 'openRate', 'clickRate', 'unsubscribeRate'];

export interface SortPref { key: string; dir: 'asc' | 'desc' }

export interface ScreenPrefs {
    /** Os blocos da tela, na ordem de desenho. */
    blocks: ScreenBlock[];
    /** Os blocos desligados. A lista nunca entra aqui. */
    hidden: ScreenBlock[];
    /** Indicadores do topo, na ordem escolhida. */
    kpis: string[];
    kpiStyle: KpiStyle;
    /** Modelo do gráfico desta tela — a chave vale dentro do catálogo dela. */
    chart: string;
    /** Colunas visíveis, na ordem. */
    cols: string[];
    view: string;
    /** `null` = a ordem que a API já mandou, que é a ordem certa por padrão. */
    sort: SortPref | null;
    /** Templates: mostrar arquivados (desligado por padrão). */
    showArchived?: boolean;
    /** "Separar por origem": vale para os dois desenhos — faixa na tabela, faixa no card. */
    grouped?: boolean;
    /** Tabela ou cards. */
    display: Display;
    /** Os blocos ligados no card; a ordem de desenho é a de `CARD_BLOCKS`. */
    cardBlocks: CardBlock[];
    /** As métricas do card, na ordem escolhida. Independentes das colunas da tabela. */
    cardMetrics: string[];
    cardSize: CardSize;
}

export interface OverviewPrefs {
    blocks: OverviewBlock[];
    hidden: OverviewBlock[];
    kpis: string[];
    kpiStyle: KpiStyle;
    chart: ChartModel;
    /** A métrica de "Faixa normal". */
    metric: string;
    /** As duas de "Métrica × métrica": esquerda e direita. */
    pairA: string;
    pairB: string;
}

export interface EmailPrefs {
    currency: Currency;
    overview: OverviewPrefs;
    screens: Record<ScreenKey, ScreenPrefs>;
}

const defaultScreen = (k: ScreenKey): ScreenPrefs => ({
    blocks: [...SCREEN_BLOCKS], hidden: [...DEFAULT_HIDDEN_BLOCKS],
    kpis: [...DEFAULT_SCREEN_KPIS], kpiStyle: 'cards', chart: 'rank',
    cols: [...DEFAULT_COLUMNS], view: defaultView(k), sort: null, grouped: false,
    display: 'table', cardBlocks: [...CARD_BLOCKS], cardMetrics: [...DEFAULT_CARD_METRICS], cardSize: 'md',
});

export const DEFAULT_PREFS: EmailPrefs = {
    currency: 'BRL',
    overview: { blocks: [...OVERVIEW_BLOCKS], hidden: [], kpis: [...DEFAULT_KPIS], kpiStyle: 'cards', chart: 'dual', metric: 'sent', pairA: 'net', pairB: 'openRate' },
    screens: Object.fromEntries(SCREENS.map((s) => [s.k, defaultScreen(s.k)])) as Record<ScreenKey, ScreenPrefs>,
};

const obj = (x: unknown): Record<string, unknown> => (x && typeof x === 'object' && !Array.isArray(x) ? x as Record<string, unknown> : {});
const strList = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : []);
/** Mantém a ordem pedida, tira repetido e o que não existe mais no catálogo. */
const pick = (x: unknown, allowed: readonly string[], fallback: string[]): string[] => {
    const out = strList(x).filter((v, i, a) => allowed.includes(v) && a.indexOf(v) === i);
    return out.length ? out : [...fallback];
};

const mergeOverview = (raw: unknown): OverviewPrefs => {
    const o = obj(raw);
    const blocks = pick(o.blocks, OVERVIEW_BLOCKS, [...OVERVIEW_BLOCKS]) as OverviewBlock[];
    // um bloco novo do produto entra no fim em vez de sumir da tela de quem já tinha preferência
    for (const b of OVERVIEW_BLOCKS) if (!blocks.includes(b)) blocks.push(b);
    const hidden = (strList(o.hidden).filter((b): b is OverviewBlock => (OVERVIEW_BLOCKS as readonly string[]).includes(b)))
        .filter((b) => !BLOCK_META[b].locked);
    const chart = typeof o.chart === 'string' && o.chart in CHART_MODELS ? o.chart as ChartModel : 'dual';
    const kpiStyle = KPI_STYLES.some((s) => s.k === o.kpiStyle) ? o.kpiStyle as KpiStyle : 'cards';
    // a métrica do gráfico é uma das 33, e nada mais: aqui o catálogo existe e vale a conferência
    const one = (x: unknown, fallback: string) => (typeof x === 'string' && M[x] ? x : fallback);
    return {
        blocks, hidden, kpis: pick(o.kpis, KPI_CHOICES, DEFAULT_KPIS), kpiStyle, chart,
        metric: one(o.metric, 'sent'), pairA: one(o.pairA, 'net'), pairB: one(o.pairB, 'openRate'),
    };
};

const mergeScreen = (raw: unknown, k: ScreenKey): ScreenPrefs => {
    const o = obj(raw);
    const views = SCREENS.find((s) => s.k === k)?.views ?? [];
    const sortRaw = obj(o.sort);
    /**
     * A chave de ordenação não se valida contra o catálogo de métricas: Cliques ordena por
     * `clicks`, Leads por `newLeads`, e nenhuma das duas é métrica de envio — exigir `M[key]`
     * fazia a ordem escolhida nessas telas voltar ao padrão no recarregamento. Quem valida é a
     * tabela que desenha: coluna que não existe mais simplesmente não ordena.
     */
    const sort: SortPref | null = typeof sortRaw.key === 'string' && sortRaw.key
        ? { key: sortRaw.key, dir: sortRaw.dir === 'asc' ? 'asc' : 'desc' }
        : null;
    // preferência antiga (sem card nenhum) ganha o padrão; lista salva vazia vira só a identificação
    const savedBlocks = strList(o.cardBlocks)
        .filter((b, i, a) => (CARD_BLOCKS as readonly string[]).includes(b) && a.indexOf(b) === i) as CardBlock[];
    const cardBlocks = o.cardBlocks === undefined
        ? [...CARD_BLOCKS]
        : savedBlocks.includes('ident') ? savedBlocks : ['ident' as CardBlock, ...savedBlocks];
    const blocks = pick(o.blocks, SCREEN_BLOCKS, [...SCREEN_BLOCKS]) as ScreenBlock[];
    // bloco novo do produto entra no fim, em vez de sumir da tela de quem já tinha preferência
    for (const b of SCREEN_BLOCKS) if (!blocks.includes(b)) blocks.push(b);
    const hidden = (strList(o.hidden).filter((b): b is ScreenBlock => (SCREEN_BLOCKS as readonly string[]).includes(b)))
        .filter((b) => !SCREEN_BLOCK_META[b].locked);
    return {
        blocks,
        hidden: o.hidden === undefined ? [...DEFAULT_HIDDEN_BLOCKS] : hidden,
        kpis: pick(o.kpis, KPI_CHOICES, DEFAULT_SCREEN_KPIS),
        kpiStyle: KPI_STYLES.some((st) => st.k === o.kpiStyle) ? o.kpiStyle as KpiStyle : 'cards',
        // o catálogo de modelos é por tela: quem valida é quem desenha, que o conhece
        chart: typeof o.chart === 'string' && o.chart ? o.chart : 'rank',
        cols: pick(o.cols, Object.keys(M), DEFAULT_COLUMNS),
        view: views.some((v) => v.k === o.view) ? o.view as string : defaultView(k),
        sort,
        showArchived: o.showArchived === true,
        grouped: o.grouped === true,
        display: o.display === 'cards' ? 'cards' : 'table',
        cardBlocks,
        cardMetrics: pick(o.cardMetrics, Object.keys(M), DEFAULT_CARD_METRICS),
        cardSize: CARD_SIZES.some((s) => s.k === o.cardSize) ? o.cardSize as CardSize : 'md',
    };
};

export const mergePrefs = (raw: Record<string, unknown> | null | undefined): EmailPrefs => {
    const o = obj(raw);
    const screens = obj(o.screens);
    return {
        currency: o.currency === 'USD' ? 'USD' : 'BRL',
        overview: mergeOverview(o.overview),
        screens: Object.fromEntries(SCREENS.map((s) => [s.k, mergeScreen(screens[s.k], s.k)])) as Record<ScreenKey, ScreenPrefs>,
    };
};

/** O que vai para a API. O PUT tem teto de 64 KB — este objeto fica em ~1 KB. */
export const toApiPrefs = (p: EmailPrefs): Record<string, unknown> => ({ ...p });
